import { useState } from "react"

import { useTranslation } from "react-i18next"
import { useQuery } from "react-query"
import { useSelector } from "react-redux"
import { useParams } from "react-router-dom"

import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined"
import { Grid } from "@mui/material"
import { Button, Pagination, Snackbar } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"

import { DecisionLoadingCard } from "../components/cards/DecisionLoadingCard"
import { KonanPageHeader } from "../components/core/KonanPageHeader"
import { KonanSubHeader } from "../components/core/KonanSubHeader"
import { KonanEmptyState } from "../components/utility/KonanEmptyState"
import { CalculatorCard } from "../features/Calculators"
import { KonanAPI } from "../services/KonanAPI"
import type { RootState } from "../store/ReduxStore"
import type { PaginatedTagListListList } from "../types/generated/api/PaginatedTagListListList"
import { useIsPermitted } from "../utils/PermissionsHelpers"

export function Calculators(): React.ReactElement {
  const { id: projectId } = useParams<{ id: string }>()

  const permissions = useSelector((state: RootState) => state.auth.permissions)

  const flattenedKonanPermissions = useSelector((state: RootState) => state.permissions.flattenedKonanPermissions)

  const canAddCalculator = useIsPermitted("Add Calculator", permissions.konan, flattenedKonanPermissions)
  const canShowDetails = useIsPermitted("Retrieve Calculator", permissions.konan, flattenedKonanPermissions)

  const { t } = useTranslation("calculators")

  // Pagination
  const pageSize = 3
  const [page, setPage] = useState<number>(0)

  const [searchValue, setSearchValue] = useState<string>("")
  const [sortValue, setSortValue] = useState<SortKey>("mostRecent")

  const [duplicatedId, setDuplicatedId] = useState<string | null>(null)

  const [showCreationCard, setShowCreationCard] = useState<boolean>(false)

  // SSF Types And Constants
  type SortKey = keyof typeof SORT_OPTIONS
  const SORT_OPTIONS = {
    mostRecent: "-created_at",
    lastUsed: "-last_used",
  } as const

  // fetch calculators
  const { isLoading: isCalculatorsLoading, data: calculators } = useQuery<
    AxiosResponse<PaginatedTagListListList>,
    AxiosError
  >(
    ["calculators", projectId, page, pageSize, searchValue, sortValue],
    () =>
      KonanAPI.listCalculators({
        projectId: projectId as string,
        page: page + 1,
        page_size: pageSize,
        search: searchValue,
        ordering: SORT_OPTIONS[sortValue],
      }),
    {
      enabled: !!projectId,
    },
  )

  const duplicateCalculator = (uuid: string): void => {
    setDuplicatedId(uuid)
    // scroll to top
    window.scrollTo(0, 0)
  }

  return (
    <Grid item container spacing={2}>
      <Grid container item xs={12} textAlign={"right"} gap={2}>
        <KonanPageHeader title={t("pageHeaderTitle")} />
        <KonanSubHeader
          searchValue={searchValue}
          sortValue={t(sortValue)}
          setSearchValue={(value) => {
            setPage(() => 0)
            setSearchValue(() => value as string)
          }}
          sortOptions={[
            { label: t("mostRecent"), value: "mostRecent" },
            { label: t("lastUsed"), value: "lastUsed" },
          ]}
          setSortValue={(value) => {
            setPage(() => 0)
            setSortValue(value as SortKey)
          }}
          containerType={"Calculator"}
          isDebouncedInput
          actions={
            canShowDetails && canAddCalculator
              ? [
                  <Button
                    key="Create_calculator"
                    variant="primary"
                    startIcon={<AddCircleOutlineOutlinedIcon fontSize="small" />}
                    onClick={() => setShowCreationCard(true)}
                    disabled={showCreationCard || isCalculatorsLoading || !!duplicatedId}
                    size="regular"
                  >
                    {t("createCalculatorBtn")}
                  </Button>,
                ]
              : undefined
          }
        />
      </Grid>

      {!canShowDetails && (
        <Grid item flex={1}>
          <Snackbar
            fullWidth
            severity="info"
            title={t("cannotShowDetailsSnackBar.title")}
            description={t("cannotShowDetailsSnackBar.description")}
          />
        </Grid>
      )}

      {/* Calculators empty state */}
      {!!projectId && !isCalculatorsLoading && !calculators?.data.count && !showCreationCard && (
        <Grid item xs={12} className={"empty-container"}>
          <KonanEmptyState
            title={t("emptyState.title")}
            subTitle={t("emptyState.subtitle")}
            buttonText={canAddCalculator ? t("emptyState.addCalculatorBtn") : undefined}
            setAction={setShowCreationCard}
          />
        </Grid>
      )}

      {!isCalculatorsLoading && (!!duplicatedId || showCreationCard) && (
        <Grid item xs={12} key={!!duplicatedId ? `duplicate-${duplicatedId}` : "new"}>
          <CalculatorCard
            uuid={duplicatedId ?? undefined}
            isDuplicate
            canShowDetails={canShowDetails}
            onClose={() => (duplicatedId ? setDuplicatedId(null) : setShowCreationCard(false))}
          />
        </Grid>
      )}

      {/* Loading state */}
      {isCalculatorsLoading
        ? [1, 2].map((item: number) => <DecisionLoadingCard key={item} />)
        : calculators?.data.results?.map((calculator) => (
            <Grid item xs={12} key={calculator.uuid}>
              <CalculatorCard
                uuid={calculator.uuid}
                onDuplicateClicked={() => duplicateCalculator(calculator.uuid)}
                shouldEnableDuplicate={!duplicatedId && !showCreationCard}
                metaData={calculator}
                canShowDetails={canShowDetails}
              />
            </Grid>
          ))}

      {/* Pagination Section */}
      {!isCalculatorsLoading && calculators?.data.results && calculators?.data.results.length > 0 && (
        <Grid item>
          <Pagination
            page={page + 1}
            pageSize={pageSize}
            totalCount={calculators.data.count as number}
            onPageChange={(page: number) => {
              setPage(page - 1)
              window.scrollTo(0, 0)
            }}
            variant={"complex"}
            hidePageSize
          />
        </Grid>
      )}
    </Grid>
  )
}
